import type { ChangeEvent } from 'react';
import { useState, useRef } from 'react';
import { useDebounce, useAsyncFn } from 'react-use';
import type {
    IAsset,
    TConfigUrlToAssetTemplatePage,
} from '@alfa-bank/site-invest-emitents-common/src/models';

import type { IFindAssetArgs } from '../api/find-assets';
import { findAssets } from '../api/find-assets';
import { MAX_SEARCH_SIZE, MIN_SEARCH_SIZE, SEARCHING_FETCH_DEBOUNCE_TIME } from '../constants';

export type THandlerArgs = {
    event: ChangeEvent<HTMLInputElement>;
    name?: string | undefined;
    value: string;
};

interface IUseSearchAssets {
    configUrlToAssetTemplatePage: TConfigUrlToAssetTemplatePage;
    limit: number;
    text?: string;
}

type TRequest = Pick<IFindAssetArgs, 'controller' | 'value'>;

/** Длина строки подходит под минимальный поисковый запрос */
const isSearchedValueLengthMinSizeValid = (value: string) => value.length >= MIN_SEARCH_SIZE;
/** Длина строки подходит под максимальный поисковый запрос */
const isSearchedValueLengthMaxSizeValid = (value: string) => value.length <= MAX_SEARCH_SIZE;
/** Длина строки подходит под поисковый запрос */
const isSearchedValueLengthValid = (value: string) =>
    isSearchedValueLengthMinSizeValid(value) && isSearchedValueLengthMaxSizeValid(value);

export const useSearchAssets = <T extends IAsset>({
    configUrlToAssetTemplatePage,
    limit,
    text = '',
}: IUseSearchAssets) => {
    const [searchValue, setSearchValue] = useState(text);
    const [searchResult, setSearchResult] = useState({});
    const [searchCount, setSearchCount] = useState<number | null>(null);
    const [isShowSuggestions, setIsShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const forwardedRef = useRef<HTMLInputElement>(null);
    const prevRequest = useRef<TRequest[]>([]);

    const showSuggestions = () => setIsShowSuggestions(true);
    const hideSuggestions = () => setIsShowSuggestions(false);

    const onChange = (e: THandlerArgs) => {
        const { value } = e;
        setSearchValue(value);
        if (isSearchedValueLengthMinSizeValid(value)) {
            showSuggestions();
        }
    };

    const onClear = () => {
        setSearchValue('');
        setSearchResult({});
        hideSuggestions();
        forwardedRef.current?.focus();
    };

    const onFocus = () => {
        if (isSearchedValueLengthMinSizeValid(searchValue)) {
            showSuggestions();
        }
    };

    const [, fetch] = useAsyncFn(async () => {
        setIsLoading(true);
        const cleanedValue = searchValue.trim();
        if (isSearchedValueLengthValid(cleanedValue)) {
            try {
                const controller = new AbortController();

                // istanbul ignore next
                prevRequest.current.forEach(request => request.controller.abort());
                prevRequest.current.length = 0;

                prevRequest.current.push({ value: cleanedValue, controller });

                const data = await findAssets<T>({
                    limit,
                    value: cleanedValue,
                    controller,
                    configUrlToAssetTemplatePage,
                });

                if (data) {
                    setSearchResult(data.assets);
                    setSearchCount(data.count);
                }
            } catch (error) {
                // Если при запросе происходит ошибка, то мы там ее отлавливаем и сюда не попадаем
                // Сюда пока нет никаких шансов попасть, возможно стоит ошибку кидать из findAsset, чтобы тут в catch попасть
                // istanbul ignore next
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        }
    }, [searchValue]);

    useDebounce(
        () => {
            if (searchValue.length < MIN_SEARCH_SIZE) {
                setSearchResult({});
                setSearchCount(null);
            } else {
                fetch();
            }
        },
        SEARCHING_FETCH_DEBOUNCE_TIME,
        [searchValue]
    );

    return {
        onClear,
        onChange,
        searchCount,
        searchResult,
        searchValue,
        forwardedRef,
        isShowSuggestions,
        onFocus,
        isLoading,
        hideSuggestions,
    };
};
